import * as React from 'react';
import { Loader } from '../../loading/Loader';
import { css } from '@emotion/core';
import { COLORS } from '../../globalStyle/colors';
import { useWindowSize } from '../../core/hooks/useWindowSize';
import { Str } from '../../localization/translationKeys';
import Msg from '../../localization/Msg';
import { Report } from './models';
import { useSelector } from 'react-redux';
import { AppState } from '../../core/redux/appState';
import { ParticipantProject } from '../models';
import { useAppState } from '../../core/redux/hooks';
import { Language } from '../../localization/models';
import FeedbackReportTableData from './FeedbackReportTableData';
import ReportCards from './ReportCards';
import { FLEX_COLUMN } from '../../layout/flexBox';


const styles = {
  root:      css`
    display: flex;
  `,
  loader:    css`
    width: 150px;
    height: 150px;
    margin: auto;
  `,
  error:     css`
    color: ${COLORS.WHITE};
    background-color: ${COLORS.RED};
    padding: 15px 20px;
  `,
  noReports: css`
    text-align: center;
  `,
  table: css`
    width: 100%;
  `,
  cards:     [
    FLEX_COLUMN,
    css`
      align-items: stretch;
      align-self: stretch;
      flex-basis: 100%;
    `,
  ],
};


type Props = {
  project: ParticipantProject | undefined;
  reports: Report[] | undefined;
  code: string;

  showShareScoresColumn: boolean;
  loading?: boolean;
  error?: boolean;

  showShareScoresBtn: (report: Report) => boolean;
  enableReportDownload: (report: Report) => boolean;
  setReport: (report: Report) => void;
  onClickShareScoresBtn: (report: Report) => void;

  className?: string;
};

const renderEdgeCase = (props: Props): React.ReactNode => {
  if (props.loading) {
    return <Loader css={styles.loader} />;
  }

  if (props.error) {
    return <Msg str={Str.LoadFailed} css={styles.error} />;
  }

  if (!props.reports || props.reports.length <= 0) {
    return <Msg str={Str.NoReports} css={styles.error} />;
  }

  return null;
};

const renderData = (
  props: Props,
  isMobile: boolean,
  participantId: number,
  getLanguage: (languageId: number) => Language,
): React.ReactNode => {
  const reports = props.reports ?? [];

  return isMobile
    ? (
      <ReportCards
        reports={reports}
        showShareScoresBtn={props.showShareScoresBtn}
        showDownloadReportBtn={props.enableReportDownload}
        setReport={props.setReport}
        onClickShareScoresBtn={props.onClickShareScoresBtn}
        getLanguage={getLanguage}
        css={styles.cards}
      />
    ) : (
      <FeedbackReportTableData
        reports={reports}
        showShareScoresColumn={props.showShareScoresColumn}
        showShareScoresBtn={props.showShareScoresBtn}
        showDownloadReportBtn={props.enableReportDownload}
        setReport={props.setReport}
        onClickShareScoresBtn={props.onClickShareScoresBtn}
        getLanguage={getLanguage}
        code={props.code}
        css={styles.table}
      />
      );
};


/**
 * ReportTable: a component that renders the given list of reports in a navigable way. Can also render loading/error
 * state.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportTable: React.FC<Props> = props => {
  const { isMobile }  = useWindowSize();
  const edgeCase      = renderEdgeCase(props);
  const participantId = useSelector<AppState, number>(state => state.auth.currentUser?.personId ?? 0);

  const allLangs      = useAppState(state => state.allLanguages ?? []);

  const getLanguage = (languageId: number) => (
    allLangs.find(lang => lang.languageId === languageId)!
  );

  return (
    <div css={styles.root} className={props.className}>
      { // Full
        edgeCase ?? renderData(props, isMobile, participantId, getLanguage)
      }
    </div>
  );
};

export default ReportTable;
